import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { badgesApi, paymentsApi, referralsApi, profileApi, aiApi } from '../services/api';
import {
  Trophy,
  Flame,
  Target,
  Calendar,
  Award,
  Loader2,
  Crown,
  Zap,
  AlertTriangle,
  Mail,
  Copy,
  Check,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import AuthModal from '../components/AuthModal';
import SkillRadarChart from '../components/SkillRadarChart';
import Footer from '../components/Footer';
import { ProfileSubscriptionCard } from '../components/ProfileSubscriptionCard';
import { ProfileReferralCard } from '../components/ProfileReferralCard';
import { ProfileHintQuotaCard } from '../components/ProfileHintQuotaCard';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const Profile = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, refreshProfile } = useAuth();
  const [badges, setBadges] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [referral, setReferral] = useState(null);
  const [referralCopied, setReferralCopied] = useState(false);
  const [applyCode, setApplyCode] = useState('');
  const [applying, setApplying] = useState(false);
  const [applyMsg, setApplyMsg] = useState(null);
  const [applyError, setApplyError] = useState(null);
  const [skillRadar, setSkillRadar] = useState(null);
  const [hintQuota, setHintQuota] = useState(null);
  const [emailCopied, setEmailCopied] = useState(false);

  const handleCopyEmail = async () => {
    if (!user?.email) return;
    try {
      await navigator.clipboard.writeText(user.email);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 1500);
    } catch (err) {
      console.debug('[profile] copy email failed:', err?.message);
    }
  };

  const handleCancelPro = async () => {
    setCancelError(null);
    try {
      setCancelling(true);
      const result = await paymentsApi.cancel();
      setSubscription((prev) => ({ ...(prev || {}), ...result }));
      await refreshProfile();
      setShowCancel(false);
    } catch (err) {
      setCancelError(err.message || 'Could not cancel');
    } finally {
      setCancelling(false);
    }
  };

  const referralLink = referral?.code
    ? `${window.location.origin}/?ref=${referral.code}`
    : '';

  const handleCopyReferral = async () => {
    if (!referral?.code) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setReferralCopied(true);
      setTimeout(() => setReferralCopied(false), 2000);
    } catch (err) {
      console.error('clipboard failed', err);
    }
  };

  const handleShareReferral = async () => {
    if (!referral?.code) return;
    const shareData = {
      title: 'CyberInsti — Learn to hack, defend, repeat.',
      text: `Join me on CyberInsti. Use my code ${referral.code} and we both get +14 days of Pro.`,
      url: referralLink,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        // user cancelled or not supported — fall back to copy.
        console.debug('[profile] navigator.share failed:', err?.message);
      }
    }
    handleCopyReferral();
  };

  const handleApplyCode = async () => {
    setApplyError(null);
    setApplyMsg(null);
    const code = applyCode.trim();
    if (!code) {
      setApplyError('Enter a code to apply');
      return;
    }
    try {
      setApplying(true);
      const result = await referralsApi.apply(code);
      if (result.already_applied) {
        setApplyMsg(`A referral code is already attached (${result.referredBy}).`);
      } else {
        setApplyMsg(`Code applied! Start your Pro trial to unlock +14 days for you and your friend.`);
      }
      setApplyCode('');
      await refreshProfile();
      // refresh referral stats
      try {
        const r = await referralsApi.getMe();
        setReferral(r);
      } catch (err) {
        // non-fatal — stats will refresh on next mount.
        console.debug('[profile] referral stats refresh failed:', err?.message);
      }
    } catch (err) {
      setApplyError(err.message || 'Could not apply code');
    } finally {
      setApplying(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const badgesData = await badgesApi.getAll();
        setBadges(badgesData);

        // Refresh user profile to get latest data
        if (isAuthenticated) {
          await refreshProfile();
          try {
            const sub = await paymentsApi.getSubscription();
            setSubscription(sub);
          } catch (err) {
            // non-fatal — subscription card just won't render.
            console.debug('[profile] subscription fetch failed:', err?.message);
          }
          try {
            const ref = await referralsApi.getMe();
            setReferral(ref);
          } catch (err) {
            // non-fatal — referral card just won't render.
            console.debug('[profile] referral fetch failed:', err?.message);
          }
          try {
            const radar = await profileApi.getSkillRadar();
            setSkillRadar(radar);
          } catch (err) {
            // non-fatal — radar card shows upsell/loading.
            console.debug('[profile] skill radar fetch failed:', err?.message);
          }
          try {
            const q = await aiApi.getHintQuota();
            setHintQuota(q);
          } catch (err) {
            // non-fatal — hint quota card just won't render.
            console.debug('[profile] hint quota fetch failed:', err?.message);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAuthenticated, refreshProfile]);

  // Show auth prompt if not logged in
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      setShowAuthModal(true);
    }
  }, [authLoading, isAuthenticated]);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00FF00]" />
      </div>
    );
  }

  const userStats = isAuthenticated && user ? {
    username: user.username || 'User',
    rank: user.rank || 0,
    points: user.points || 0,
    streak: user.streak || 0,
    badges: user.badges?.length || 0,
    skillLevel: user.skillLevel || 'Beginner',
    joinDate: user.joinDate || new Date().toISOString(),
    completedRooms: user.completedRooms || [],
  } : {
    username: 'Guest',
    rank: 0,
    points: 0,
    streak: 0,
    badges: 0,
    skillLevel: 'Beginner',
    joinDate: new Date().toISOString(),
    completedRooms: [],
  };

  // Map user's badge IDs to badge objects
  const userBadges = badges.map(badge => ({
    ...badge,
    earned: user?.badges?.includes(badge.id) || false
  }));

  // Default badges if no badges from API
  const displayBadges = userBadges.length > 0 ? userBadges : [
    { id: 1, name: 'First Steps', description: 'Complete your first room', icon: '🎯', earned: false },
    { id: 2, name: 'Streak Master', description: '7 day login streak', icon: '🔥', earned: false },
    { id: 3, name: 'Linux Guru', description: 'Complete all Linux rooms', icon: '🐧', earned: false },
    { id: 4, name: 'Web Hacker', description: 'Master web exploitation', icon: '🌐', earned: false },
    { id: 5, name: 'AI Defender', description: 'Complete AI threat detection path', icon: '🤖', earned: false },
    { id: 6, name: 'CTF Champion', description: 'Win 10 CTF challenges', icon: '🏆', earned: false }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-16">
        {/* Profile Header */}
        <Card className="bg-[#1A1A1A] border-gray-800 mb-6">
          <CardContent className="p-8">
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#00FF00] to-[#00BFFF] flex items-center justify-center text-4xl font-bold text-black">
                {userStats.username.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-3xl font-bold mb-2 text-white">{userStats.username}</h1>
                {user?.email && (
                  <button
                    type="button"
                    onClick={handleCopyEmail}
                    data-testid="profile-email-copy"
                    title="Click to copy email"
                    className="group inline-flex items-center gap-2 mb-3 px-2.5 py-1 rounded-md border border-gray-800 bg-[#0F0F0F] hover:border-[#00FF00]/40 hover:bg-[#00FF00]/5 transition-colors"
                  >
                    <Mail className="w-3.5 h-3.5 text-[#00FF00]" />
                    <span
                      data-testid="profile-email-text"
                      className="font-mono text-sm text-gray-200 group-hover:text-white truncate"
                    >
                      {user.email}
                    </span>
                    {emailCopied ? (
                      <Check className="w-3.5 h-3.5 text-[#00FF00]" />
                    ) : (
                      <Copy className="w-3.5 h-3.5 text-gray-500 group-hover:text-[#00FF00]" />
                    )}
                  </button>
                )}
                <div className="flex items-center gap-4 text-gray-400 flex-wrap">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm">
                      Joined {new Date(userStats.joinDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </span>
                  </div>
                  <Badge className="bg-[#00FF00]/20 text-[#00FF00] border-[#00FF00]">
                    {userStats.skillLevel}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription */}
        <ProfileSubscriptionCard
          subscription={subscription}
          onExtend={() => navigate('/pricing')}
          onCancel={() => setShowCancel(true)}
        />

        {/* AI hint quota — surfaces conversion intent (free user near/at 0
            remaining = highest-intent upgrade moment in the funnel). */}
        {isAuthenticated && (
          <ProfileHintQuotaCard
            quota={hintQuota}
            onUpgrade={() => navigate('/pricing')}
          />
        )}
        {/* Refer & Earn */}
        {isAuthenticated && (
          <ProfileReferralCard
            referral={referral}
            referralLink={referralLink}
            referralCopied={referralCopied}
            applyCode={applyCode}
            applying={applying}
            applyError={applyError}
            applyMsg={applyMsg}
            userReferredBy={user?.referredBy}
            onCopy={handleCopyReferral}
            onShare={handleShareReferral}
            onApplyCodeChange={(val) => {
              setApplyCode(val);
              setApplyError(null);
              setApplyMsg(null);
            }}
            onApplyCode={handleApplyCode}
          />
        )}
        <div className="grid md:grid-cols-4 gap-6 mb-6">
          <Card className="bg-[#1A1A1A] border-gray-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-400">Global Rank</CardTitle>
                <Trophy className="w-5 h-5 text-[#FFD700]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">
                {userStats.rank > 0 ? `#${userStats.rank}` : 'Unranked'}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1A1A1A] border-gray-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-400">Total Points</CardTitle>
                <Target className="w-5 h-5 text-[#00FF00]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{userStats.points}</div>
            </CardContent>
          </Card>

          <Card className="bg-[#1A1A1A] border-gray-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-400">Streak</CardTitle>
                <Flame className="w-5 h-5 text-[#FF9500]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{userStats.streak}</div>
              <p className="text-xs text-gray-500 mt-1">days</p>
            </CardContent>
          </Card>

          <Card className="bg-[#1A1A1A] border-gray-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-400">Badges</CardTitle>
                <Award className="w-5 h-5 text-[#00BFFF]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{userStats.badges}</div>
              <p className="text-xs text-gray-500 mt-1">earned</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Badges Section */}
          <Card className="bg-[#1A1A1A] border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Achievements & Badges</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {displayBadges.map((badge) => (
                  <div 
                    key={badge.id}
                    className={`border rounded-lg p-4 text-center transition-all ${
                      badge.earned 
                        ? 'border-[#00FF00]/50 bg-[#00FF00]/5 hover:border-[#00FF00] cursor-pointer' 
                        : 'border-gray-800 bg-gray-900/30 opacity-50'
                    }`}
                  >
                    <div className="text-4xl mb-2">{badge.icon}</div>
                    <div className="font-semibold text-white text-sm mb-1">{badge.name}</div>
                    <div className="text-xs text-gray-400">{badge.description}</div>
                    {badge.earned && (
                      <Badge className="mt-2 bg-[#00FF00] text-black border-0 text-xs">
                        Earned
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Skill Radar (live data) */}
          <Card
            className="bg-[#1A1A1A] border-gray-800"
            data-testid="skill-radar-card"
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Skill Radar</CardTitle>
                {user?.tier === 'pro' ? (
                  <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-[#00FF00]/40">
                    PRO
                  </Badge>
                ) : (
                  <Badge className="bg-gray-800 text-gray-400 border-gray-700">
                    FREE
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {user?.tier === 'pro' ? (
                skillRadar ? (
                  <div className="flex flex-col items-center">
                    <SkillRadarChart
                      categories={skillRadar.categories || []}
                      size={320}
                    />
                    <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-400 w-full max-w-sm">
                      {(skillRadar.categories || []).map((c) => (
                        <div
                          key={c.name}
                          className="flex justify-between"
                          data-testid={`skill-cat-${c.name.replace(/\s+/g,'-').toLowerCase()}`}
                        >
                          <span>{c.name}</span>
                          <span className="text-white font-mono">
                            {c.completed}/{c.total}{' '}
                            <span className="text-[#00FF00]">({c.percent}%)</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 text-center py-10">
                    Loading skill radar…
                  </div>
                )
              ) : (
                <div className="py-8 text-center" data-testid="skill-radar-upsell">
                  <Crown className="w-10 h-10 text-[#00FF00] mx-auto mb-3" />
                  <p className="text-white font-semibold mb-1">
                    Skill Radar is a Pro feature
                  </p>
                  <p className="text-xs text-gray-400 mb-4 max-w-xs mx-auto">
                    Visualize your strengths across Linux, Web Security, Networking
                    and more. Upgrade to unlock.
                  </p>
                  <Button
                    onClick={() => navigate('/pricing')}
                    className="bg-[#00FF00] text-black hover:bg-[#00DD00] font-semibold"
                    data-testid="skill-radar-upgrade-btn"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Upgrade to Pro
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Completed Rooms */}
        <Card className="bg-[#1A1A1A] border-gray-800 mt-6">
          <CardHeader>
            <CardTitle className="text-white">Completed Rooms</CardTitle>
          </CardHeader>
          <CardContent>
            {userStats.completedRooms.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {userStats.completedRooms.map((roomId) => (
                  <div 
                    key={roomId}
                    onClick={() => navigate(`/room/${roomId}`)}
                    className="border border-[#00FF00]/30 bg-[#00FF00]/5 rounded-lg p-4 cursor-pointer hover:border-[#00FF00] transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#00FF00]" />
                      <span className="text-white font-medium">{roomId}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <p>Complete more rooms to see them here</p>
                <p className="text-sm mt-2">0 rooms completed</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {showAuthModal && (
        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)}
          initialTab="login"
        />
      )}

      <Dialog open={showCancel} onOpenChange={setShowCancel}>
        <DialogContent
          className="bg-[#1A1A1A] border-gray-800 text-white"
          data-testid="cancel-pro-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <AlertTriangle className="w-5 h-5 text-[#FF9500]" />
              End Pro access?
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              You will immediately lose access to the interactive terminal, AI hints, and unlimited rooms.
              Your progress and points are kept.
            </DialogDescription>
          </DialogHeader>
          {cancelError && (
            <p className="text-sm text-red-400" data-testid="cancel-error">{cancelError}</p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCancel(false)}
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
              data-testid="cancel-pro-keep-btn"
            >
              Keep Pro
            </Button>
            <Button
              onClick={handleCancelPro}
              disabled={cancelling}
              className="bg-[#FF0055] text-white hover:bg-[#DD0044]"
              data-testid="cancel-pro-confirm-btn"
            >
              {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'End Pro now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
};

export default Profile;
